import { Typography, Chip, Stack, Link } from '@mui/material';
import { DetailSection, DetailField } from './DetailSection';
import { severityChipSx, stateChipSx } from './severity';
import { CK, statusChipSx } from '../portalStyles';

const fmt = (t) => {
  if (!t) return '';
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
};

// The ANALYSIS RUN's progress — InvestigationDetail.Status — and nothing to do
// with triage. It is carried on the Detective-specific detail struct precisely
// because it has no home in NormalizedFinding: mapping it onto workflowStatus
// would render a RUNNING investigation as an archived finding, the exact opposite
// of what running means. An unknown value passes through raw in the neutral
// colour, so a status AWS adds later surfaces as itself rather than as "Running".
const ANALYSIS_STATUS = {
  SUCCESSFUL: { label: 'Successful', intent: 'success' },
  RUNNING: { label: 'Running', intent: 'info' },
  FAILED: { label: 'Failed', intent: 'error' },
};

// AWS documents no URL for a single investigation report: the console route is
// Investigations → pick the ID. So this links to the Detective console for the
// finding's OWN region — the behavior graph is per-region and the investigation
// belongs to one graph — and the copy tells the operator which ID to open rather
// than pretending the link lands on it.
const detectiveConsoleUrl = (region) => (region
  ? `https://${region}.console.aws.amazon.com/detective/home?region=${encodeURIComponent(region)}`
  : 'https://console.aws.amazon.com/detective/');

// Amazon Detective investigation body.
//
// Detective's "findings" are investigation reports, and two of the fields on this
// screen exist nowhere else in the portal:
//
//   * `status` is the ANALYSIS RUN (RUNNING | FAILED | SUCCESSFUL). It is rendered
//     in its own section, labelled as the analysis run, and kept visually apart
//     from the record state — this screen is the last line of defence against the
//     status/state conflation the engine adapter guards in code.
//   * the scope window (`scopeStartTime` / `scopeEndTime`) arrives as JSON **null**,
//     not as a year-1 date, for any investigation whose detail has never been
//     fetched. The whole row is omitted in that case; a partial window or an epoch
//     would render as a real date.
//
// In practice the detail route's GetInvestigation call fires on the very open that
// renders this page, so a populated window is the normal case — but that call is
// best-effort and never fails the read, so the empty branch is reachable on a
// throttle, a deleted investigation or a missing behavior-graph ARN and must read
// as an absent row, not a broken one.
//
// Prism exposes NO mutation for an investigation (it does not carry
// detective:UpdateInvestigationState), so nothing here may imply an action: the
// drawer renders no save button for this engine and the console is where the
// indicators and the response actions actually live.
export default function DetectiveDetailBody({ finding }) {
  // No null return, and no early exit: the drawer only mounts this with a
  // finding, and DetailField already omits its own row for an empty value.
  const f = finding || {};
  const remediation = f.remediation || {};

  const status = f.status || '';
  const statusMeta = ANALYSIS_STATUS[status];

  // Both ends or no row. `fmt` also returns '' for an unparseable value, so this
  // one guard covers the null case and the malformed one.
  const scopeStart = fmt(f.scopeStartTime);
  const scopeEnd = fmt(f.scopeEndTime);
  const scopeWindow = scopeStart && scopeEnd ? `${scopeStart} → ${scopeEnd}` : '';

  return (
    <>
      <DetailSection title="Overview">
        <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.75 }}>
          {/* An un-enriched investigation carries no severity at all. That is an
              "unrated" state — AWS declined to rate it, or Prism has not yet
              fetched the report that carries the rating — and folding it into
              INFORMATIONAL would put a real-looking rating on a row that has
              none. */}
          <Chip
            size="small"
            label={f.severity || 'Unrated'}
            sx={f.severity ? severityChipSx(f.severity) : stateChipSx}
          />
          {f.recordState && <Chip size="small" label={f.recordState} sx={stateChipSx} />}
        </Stack>
        {f.description && (
          <Typography variant="body2" sx={{ mb: 1.5, color: CK.navy }}>
            {f.description}
          </Typography>
        )}
        {/* investigationId, NOT findingId. findingId is Prism's own synthetic UUID
            — the id GetFinding is addressed by — and AWS has never heard of it.
            Labelling that "Investigation ID" sent the operator to the Detective
            console to look up an id that does not exist there. There is
            deliberately NO fallback to findingId: if the detail fetch has not
            supplied the real id, DetailField omits the row, and showing nothing
            beats showing an id that will not resolve. */}
        <DetailField label="Investigation ID" value={f.investigationId} />
        <DetailField label="Account" value={f.accountId} />
        <DetailField label="Region" value={f.region} />
        <DetailField label="First seen" value={fmt(f.firstObservedAt)} />
        <DetailField label="Last seen" value={fmt(f.lastObservedAt)} />
      </DetailSection>

      <DetailSection title="Entity">
        <DetailField label="Entity type" value={f.resourceType} />
        <DetailField label="Entity" value={f.resource} />
      </DetailSection>

      {(status || scopeWindow) && (
        <DetailSection title="Analysis run">
          {/* Said in words, not just implied by the section title: the chip above
              in the drawer header is the investigation's state (Active/Archived),
              and this one is the progress of Detective's analysis. They are
              different axes and a RUNNING investigation is an ACTIVE one. */}
          <Typography variant="body2" sx={{ mb: 1.5, color: CK.muted }}>
            How far Detective&apos;s analysis of this entity got. This is not the
            investigation&apos;s state — a running investigation is still active.
          </Typography>
          {status && (
            <DetailField
              label="Run status"
              value={(
                <Chip
                  size="small"
                  label={statusMeta ? statusMeta.label : status}
                  sx={statusChipSx(statusMeta ? statusMeta.intent : 'neutral')}
                />
              )}
            />
          )}
          {/* Omitted entirely when either end is missing. Never a half window and
              never an epoch: the columns are nullable so "not fetched yet" is
              null on the wire rather than a date that looks real. */}
          <DetailField label="Scope window" value={scopeWindow} />
        </DetailSection>
      )}

      <DetailSection title="Amazon Detective console">
        <Typography variant="body2" sx={{ mb: 1, color: CK.navy }}>
          The full indicator list, and any action on this investigation, live in the
          Amazon Detective console. AWS publishes no direct URL for a single report —
          open <b>Investigations</b> in the console
          {f.investigationId ? ` and select ID ${f.investigationId}` : ''}.
        </Typography>
        <Link href={detectiveConsoleUrl(f.region)} target="_blank" rel="noreferrer">
          {f.region ? `Open Amazon Detective (${f.region})` : 'Open Amazon Detective'}
        </Link>
      </DetailSection>

      {(remediation.text || remediation.url) && (
        <DetailSection title="Next steps">
          {remediation.text && (
            <Typography variant="body2" sx={{ mb: 0.5, color: CK.navy, wordBreak: 'break-word' }}>
              {remediation.text}
            </Typography>
          )}
          {remediation.url && (
            <Link href={remediation.url} target="_blank" rel="noreferrer">Read the AWS guidance</Link>
          )}
        </DetailSection>
      )}
    </>
  );
}
